import { customElement } from 'lit/decorators.js';
import { AnNamElement } from '../../base-element';

/**
 * <an-expansion-panel>
 *
 * Behavior-only component using the scheduleUpdate() suppression pattern.
 * Server-rendered children are PRESERVED — Lit never re-renders.
 * The full heading + content HTML comes from Liquid (passed as light-DOM children).
 *
 * This component wires up the toggle interaction:
 * - Reads initial open state from the "open" attribute on mount
 * - attributeChangedCallback drives is-open class on both wrapper and content div
 * - _toggle() flips the attribute, CSS handles animation via grid-template-rows
 *
 * CSS (in assets/an-nam.css) expects this DOM structure:
 *   <an-expansion-panel>
 *     <div class="an-expansion-panel">                ← wrapper, gets is-open
 *       <button class="an-expansion-panel__trigger"> ← click target
 *         <span class="an-expansion-panel__header-content">
 *           <span class="an-expansion-panel__title">       ← server-rendered heading
 *           <span class="an-expansion-panel__description"> ← server-rendered description
 *         </span>
 *         <span class="an-expansion-panel__indicator">   ← icon-caret SVG
 *       </button>
 *       <div class="an-expansion-panel__body-wrapper">
 *         <div class="an-expansion-panel__content">    ← content div, gets is-open
 *           <div class="an-expansion-panel__body">     ← padding + content
 *           </div>
 *         </div>
 *       </div>
 *     </div>
 *   </an-expansion-panel>
 */
@customElement('an-expansion-panel')
export class AnExpansionPanel extends AnNamElement {
  /** Suppress Lit render — preserve server-rendered children. */
  protected override scheduleUpdate(): void {}

  private _wrapper: HTMLElement | null = null;
  private _content: HTMLElement | null = null;
  private _trigger: HTMLButtonElement | null = null;

  override connectedCallback(): void {
    super.connectedCallback();

    // Find server-rendered elements
    this._wrapper =
      this.querySelector('.an-expansion-panel') ??
      (this.shadowRoot?.querySelector('.an-expansion-panel') as HTMLElement | null);
    this._content =
      this.querySelector('.an-expansion-panel__content') ??
      (this.shadowRoot?.querySelector('.an-expansion-panel__content') as HTMLElement | null);
    this._trigger =
      this.querySelector('.an-expansion-panel__trigger') ??
      (this.shadowRoot?.querySelector('.an-expansion-panel__trigger') as HTMLButtonElement | null);

    // Set initial open state from attribute
    this._applyOpenState(this.hasAttribute('open'));

    // Wire click on trigger button
    this._trigger?.addEventListener('click', this._toggle);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._trigger?.removeEventListener('click', this._toggle);
  }

  /** Sync is-open class when the open attribute changes (e.g. from Liquid re-render). */
  attributeChangedCallback(name: string, _old: string | null, _new: string | null): void {
    if (name !== 'open') return;
    // Defer — element may not be connected yet during construction
    Promise.resolve().then(() => {
      this._applyOpenState(this.hasAttribute('open'));
    });
  }

  private readonly _toggle = () => {
    const isOpen = this.hasAttribute('open');
    if (isOpen) {
      this.removeAttribute('open');
    } else {
      this.setAttribute('open', '');
    }
    // attributeChangedCallback fires synchronously for removeAttribute/setAttribute
    // but guard anyway
    this._applyOpenState(!isOpen);
  };

  private _applyOpenState(open: boolean): void {
    this._wrapper?.classList.toggle('is-open', open);
    this._content?.classList.toggle('is-open', open);
    this._trigger?.setAttribute('aria-expanded', String(open));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'an-expansion-panel': AnExpansionPanel;
  }
}
